import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import MDEditor from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import "@uiw/react-markdown-preview/markdown.css";
import "./styles.css";

const API_BASE =
  process.env.REACT_APP_CHAT_API_BASE || "https://reno-ts-test-backend-fhh3cpakg5daczdb.uaenorth-01.azurewebsites.net/";

const getConversationTitle = (conversation, currentUserId) => {
  const members = conversation?.members_detail || [];
  const otherMembers = members.filter(
    (member) => String(member.user_id) !== String(currentUserId)
  );

  if (otherMembers.length > 0) {
    return otherMembers
      .map((member) => member.username || member.user_id || "Unknown user")
      .join(", ");
  }

  return `Conversation #${conversation?.id ?? "Unknown"}`;
};

const formatReadBy = (readBy = []) => {
  if (!Array.isArray(readBy) || readBy.length === 0) return "";
  const visible = readBy.slice(0, 2);
  const remaining = readBy.length - visible.length;
  if (remaining > 0) {
    return `Read by ${visible.join(", ")} +${remaining} others`;
  }
  return `Read by ${visible.join(", ")}`;
};

function Conversation() {
  const { userData } = useSelector((state) => state.auth);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [messagesError, setMessagesError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const currentUsername = useMemo(() => {
    return userData?.username || userData?.email || String(userData?.id || "");
  }, [userData?.username, userData?.email, userData?.id]);

  useEffect(() => {
    const userId = userData?.id;
    if (!userId) return;

    const fetchConversations = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(
          `${API_BASE}/conversation?user_id=${encodeURIComponent(userId)}`
        );
        const payload = await response.json();

        if (!response.ok) {
          throw new Error(payload?.error || "Failed to load conversations");
        }

        const list = Array.isArray(payload?.data) ? payload.data : [];
        setConversations(list);
        setSelectedConversationId(list[0]?.id ?? null);
      } catch (err) {
        setError(err?.message || "Failed to load conversations");
      } finally {
        setLoading(false);
      }
    };

    fetchConversations();
  }, [userData?.id]);

  const refreshConversations = async () => {
    const userId = userData?.id;
    if (!userId) return;
    setLoading(true);
    setError("");
    try {
      const response = await fetch(
        `${API_BASE}/conversation?user_id=${encodeURIComponent(userId)}`
      );
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload?.error || "Failed to load conversations");
      }

      const list = Array.isArray(payload?.data) ? payload.data : [];
      setConversations(list);
      setSelectedConversationId((current) => current ?? list[0]?.id ?? null);
    } catch (err) {
      setError(err?.message || "Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedConversationId) {
      setMessages([]);
      return;
    }

    const markConversationRead = async () => {
      if (!currentUsername) return;

      try {
        await fetch(
          `${API_BASE}/conversation/${selectedConversationId}/read`,
          {
            method: "POST",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ username: currentUsername }),
          }
        );
      } catch (err) {
        // Best-effort read receipt; ignore failures.
      }
    };

    const fetchMessages = async () => {
      setMessagesLoading(true);
      setMessagesError("");
      try {
        const response = await fetch(
          `${API_BASE}/messages?conversation=${encodeURIComponent(
            selectedConversationId
          )}`
        );
        const payload = await response.json();

        if (!response.ok) {
          throw new Error(payload?.error || "Failed to load messages");
        }

        setMessages(Array.isArray(payload?.data) ? payload.data : []);
      } catch (err) {
        setMessagesError(err?.message || "Failed to load messages");
      } finally {
        setMessagesLoading(false);
      }
    };

    markConversationRead();
    fetchMessages();
  }, [selectedConversationId, currentUsername]);

  const selectedConversation = useMemo(() => {
    return conversations.find((item) => item.id === selectedConversationId) || null;
  }, [conversations, selectedConversationId]);

  const membersDetailCount = Array.isArray(selectedConversation?.members_detail)
    ? selectedConversation.members_detail.length
    : 0;
  const membersCount = Array.isArray(selectedConversation?.members)
    ? selectedConversation.members.length
    : 0;
  const isOneOnOneConversation =
    membersDetailCount === 2 || membersCount === 2;
  const otherMember =
    isOneOnOneConversation && Array.isArray(selectedConversation?.members_detail)
      ? selectedConversation.members_detail.find(
          (member) => String(member.user_id) !== String(userData?.id)
        )
      : null;
  const otherUsername = otherMember?.username || String(otherMember?.user_id || "");

  const filteredConversations = useMemo(() => {
    if (!searchTerm.trim()) return conversations;
    const query = searchTerm.trim().toLowerCase();
    return conversations.filter((conversation) => {
      const title = getConversationTitle(conversation, userData?.id).toLowerCase();
      return title.includes(query) || String(conversation.id).includes(query);
    });
  }, [conversations, searchTerm, userData?.id]);

  const handleCreateConversation = async () => {
    const createdBy = userData?.id;
    if (!createdBy) {
      setError("Missing user ID to create a conversation.");
      return;
    }
    const members = [String(createdBy)];

    try {
      const response = await fetch(`${API_BASE}/conversation`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          entityType: null,
          entityId: null,
          parentConversationId: null,
          members,
          createdBy: String(createdBy),
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to create conversation");
      }

      await refreshConversations();
    } catch (err) {
      setError(err?.message || "Failed to create conversation");
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!selectedConversationId || !newMessage.trim() || sendingMessage) return;

    const userId = userData?.id;
    if (!userId) {
      setMessagesError("Missing user ID to send a message.");
      return;
    }

    const trimmedMessage = newMessage.trim();
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage = {
      message: {
        id: tempId,
        conversationId: selectedConversationId,
        userId: String(userId),
        body: trimmedMessage,
        readBy: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      user: {
        user_id: userId,
        username: userData?.username || userData?.email || null,
        profile: userData?.profile || null,
        role: userData?.role || null,
      },
      _status: "sending",
    };
    setMessages((prev) => [...prev, optimisticMessage]);
    setSendingMessage(true);
    setMessagesError("");
    try {
      const response = await fetch(`${API_BASE}/messages`, {
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          conversationId: selectedConversationId,
          userId: String(userId),
          body: trimmedMessage,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || "Failed to send message");
      }
      const createdMessage = payload?.data;
      if (createdMessage) {
        setMessages((prev) =>
          prev.map((item) =>
            item?.message?.id === tempId
              ? {
                  ...item,
                  message: createdMessage,
                  _status: "sent",
                }
              : item
          )
        );
      }
      setNewMessage("");
    } catch (err) {
      setMessagesError(err?.message || "Failed to send message");
      setMessages((prev) =>
        prev.filter((item) => item?.message?.id !== tempId)
      );
    } finally {
      setSendingMessage(false);
    }
  };

  const sortedMessages = useMemo(() => {
    return [...messages].sort((a, b) => {
      const aTime = new Date(a?.message?.createdAt || 0).getTime();
      const bTime = new Date(b?.message?.createdAt || 0).getTime();
      return aTime - bTime;
    });
  }, [messages]);

  const groupedMessages = useMemo(() => {
    return sortedMessages.reduce((acc, item) => {
      const createdAt = item?.message?.createdAt;
      const dateKey = createdAt
        ? new Date(createdAt).toLocaleDateString()
        : "Unknown date";
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(item);
      return acc;
    }, {});
  }, [sortedMessages]);

  return (
    <div className="conversation-page">
      <div className="conversation-page__header">
        <div>
          <div className="conversation-page__title">Messages</div>
          <div className="conversation-page__subtitle">
            All conversations in the system
          </div>
        </div>
        <div className="conversation-page__count">
          {conversations.length} conversations
        </div>
      </div>
      <div className="conversation-page__content">
      <div className="conversation-pane conversation-pane--list">
        <div className="conversation-pane__header conversation-pane__header--list">
          <span>Conversations</span>
          <button
            type="button"
            className="conversation-pane__icon-button"
            onClick={handleCreateConversation}
            aria-label="Create conversation"
          >
            +
          </button>
        </div>
        <div className="conversation-pane__search">
          <input
            className="conversation-pane__search-input"
            type="text"
            placeholder="Search conversations"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>
        {loading && <div className="conversation-pane__status">Loading...</div>}
        {!loading && error && (
          <div className="conversation-pane__status conversation-pane__status--error">
            {error}
          </div>
        )}
        {!loading && !error && filteredConversations.length === 0 && (
          <div className="conversation-pane__status">No conversations found.</div>
        )}
        <div className="conversation-list">
          {filteredConversations.map((conversation) => {
            const title = getConversationTitle(conversation, userData?.id);
            const isActive = conversation.id === selectedConversationId;
            const initial = title?.[0]?.toUpperCase() || "?";
            return (
              <button
                key={conversation.id}
                type="button"
                className={`conversation-list__item ${
                  isActive ? "conversation-list__item--active" : ""
                }`}
                onClick={() => setSelectedConversationId(conversation.id)}
              >
                <div className="conversation-list__avatar">{initial}</div>
                <div className="conversation-list__content">
                  <div className="conversation-list__title">{title}</div>
                  <div className="conversation-list__subtitle">
                    ID {conversation.id}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="conversation-pane conversation-pane--messages">
        <div className="conversation-pane__header conversation-pane__header--messages">
          <div>
            <div className="conversation-pane__title">
              {selectedConversation
                ? getConversationTitle(selectedConversation, userData?.id)
                : "Messages"}
            </div>
            {selectedConversation && (
              <div className="conversation-pane__subtitle">
                ID {selectedConversation.id}
              </div>
            )}
          </div>
        </div>
        {messagesLoading && (
          <div className="conversation-pane__status">Loading messages...</div>
        )}
        {!messagesLoading && messagesError && (
          <div className="conversation-pane__status conversation-pane__status--error">
            {messagesError}
          </div>
        )}
        {!messagesLoading && !messagesError && messages.length === 0 && (
          <div className="conversation-pane__status">No messages yet.</div>
        )}
        <div className="message-list">
          {Object.entries(groupedMessages).map(([dateKey, items]) => (
            <div key={dateKey} className="message-group">
              <div className="message-group__date">{dateKey}</div>
              {items.map((item) => {
                const message = item?.message;
                const author = item?.user;
                const isMine = String(message?.userId) === String(userData?.id);
                const readBy = Array.isArray(message?.readBy)
                  ? message.readBy
                  : [];
                const filteredReadBy = readBy.filter(
                  (name) => String(name) !== String(currentUsername)
                );
                const readByText =
                  isMine && !isOneOnOneConversation
                    ? formatReadBy(filteredReadBy)
                    : "";
                const isReadByOther =
                  isMine &&
                  isOneOnOneConversation &&
                  (filteredReadBy.length > 0 ||
                    filteredReadBy.some(
                      (name) => String(name) === String(otherUsername)
                    ));
                const status =
                  isMine && isOneOnOneConversation
                    ? item?._status === "sending"
                      ? "sending"
                      : isReadByOther
                      ? "read"
                      : "sent"
                    : null;
                return (
                  <div
                    key={message?.id}
                    className={`message-list__item ${
                      isMine ? "message-list__item--mine" : ""
                    }`}
                  >
                    <div className="message-list__meta">
                      {!isMine && (
                        <span className="message-list__author">
                          {author?.username || author?.user_id || "Unknown"}
                        </span>
                      )}
                    </div>
                    <div className="message-list__body">
                      <div className="message-list__markdown">
                        <ReactMarkdown>{message?.body || ""}</ReactMarkdown>
                      </div>
                    </div>
                    <div className="message-list__footer">
                      <div className="message-list__time">
                        {message?.createdAt
                          ? new Date(message.createdAt).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })
                          : ""}
                      </div>
                      {status && (
                        <div
                          className={`message-list__status message-list__status--${status}`}
                        >
                          {status === "sending" ? "✓" : "✓✓"}
                        </div>
                      )}
                    </div>
                    {readByText && (
                      <div className="message-list__read-by">{readByText}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <form className="message-input" onSubmit={handleSendMessage}>
          <div className="message-input__editor" data-color-mode="light">
            <MDEditor
              value={newMessage}
              onChange={(value) => setNewMessage(value || "")}
              placeholder="Write a message (Markdown supported)..."
              preview="edit"
              visibleDragbar={false}
              height={120}
              textareaProps={{
                disabled: !selectedConversationId || sendingMessage,
              }}
            />
          </div>
          <button
            type="submit"
            className="message-input__button"
            disabled={!newMessage.trim() || !selectedConversationId || sendingMessage}
          >
            {sendingMessage ? "Sending" : "Send"}
          </button>
        </form>
      </div>
      </div>
    </div>
  );
}

export default Conversation;
